import { Component, EventEmitter, Input, Output, OnInit, OnChanges, SimpleChanges, OnDestroy, ChangeDetectionStrategy, ViewChild } from '@angular/core';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatAutocomplete, MatAutocompleteTrigger, MatOption } from '@angular/material/autocomplete';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { AsyncPipe } from '@angular/common';
import { BehaviorSubject, combineLatest, Subscription } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

let _autoIdCounter = 0;

@Component({
  selector: 'app-single-dropdown',
  standalone: true,
  imports: [MatFormField, MatLabel, MatInput, MatAutocomplete, ReactiveFormsModule, AsyncPipe, MatOption, MatError, MatAutocompleteTrigger],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <mat-form-field class="w-full" floatLabel="always">
      <mat-label>{{ label }}</mat-label>

      <input matInput [value]="textCtrl.value" (input)="handleInput($any($event.target).value)" (blur)="handleBlur()" required [matAutocomplete]="auto" [attr.aria-owns]="autoId" />

      <mat-autocomplete #auto="matAutocomplete" [id]="autoId" [displayWith]="internalDisplayWith" [autoActiveFirstOption]="true" (optionSelected)="handleOptionSelected($event.option.value)">
        @for (option of filteredOptions$ | async; track option) {
          <mat-option (mousedown)="$event.preventDefault()" [value]="option">{{ displayValue(option) }}</mat-option>
        }
      </mat-autocomplete>

      @if (inputControl.invalid && (inputControl.touched || inputControl.dirty)) {
        <mat-error>{{ errorInputMessage }}</mat-error>
      }
    </mat-form-field>
  `,
})
export class SingleDropdownComponent<T> implements OnInit, OnChanges, OnDestroy {
  /** UI */
  @Input() label = '';
  @Input() errorInputMessage = '';

  /** Data & selection */
  @Input() dropdownOptions: T[] = [];
  @Input({ required: true }) inputControl!: FormControl<T | null>;
  @Input() displayValue: (item: T) => string = () => '';

  /** Blur notification for parent (e.g., to recompute message text) */
  @Output() onBlur = new EventEmitter<void>();

  /** Internal text box used for filtering; keeps parent FormControl<T|null> clean */
  readonly textCtrl = new FormControl<string>('', { nonNullable: true });

  /** Unique id so multiple instances won’t collide */
  readonly autoId = `auto-${++_autoIdCounter}`;

  /** Reactive options stream (recomputed on text or options list change) */
  filteredOptions$ = combineLatest([this.textCtrl.valueChanges.pipe(startWith(this.textCtrl.value)), new BehaviorSubject<T[]>(this.dropdownOptions)]).pipe(
    // The BehaviorSubject instance gets replaced in ngOnInit/ngOnChanges; see below
    map(([term, opts]) => this.filterOptions(term, opts)),
  );

  @ViewChild('auto') autocomplete!: MatAutocomplete;

  /** Private subjects/subscriptions */
  private options$ = new BehaviorSubject<T[]>([]);
  private subs = new Subscription();

  ngOnInit() {
    // Seed internal text from current selected value (useful for edit flows)
    const v = this.inputControl.value;
    this.textCtrl.setValue(v ? this.displayValue(v) : '', { emitEvent: false });

    // Build filtered stream with the "real" options$ (so we can push updates on input changes)
    this.filteredOptions$ = combineLatest([this.textCtrl.valueChanges.pipe(startWith(this.textCtrl.value)), this.options$]).pipe(map(([term, opts]) => this.filterOptions(term, opts)));

    // Initial options
    this.options$.next(this.dropdownOptions ?? []);

    // If parent patches the selected value programmatically, reflect it in the textbox
    this.subs.add(
      this.inputControl.valueChanges.subscribe((sel) => {
        const label = sel ? this.displayValue(sel) : '';
        if (label !== this.textCtrl.value) {
          this.textCtrl.setValue(label, { emitEvent: true }); // emit to refresh filtering
        }
        if (!sel) {
          this.autocomplete?.options?.forEach((o) => o.deselect());
        }
      }),
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['dropdownOptions']) {
      // Push new options, then re-emit current text to refresh the async pipe list
      this.options$.next(this.dropdownOptions ?? []);
      this.textCtrl.setValue(this.textCtrl.value, { emitEvent: true });
    }
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
    this.options$.complete();
  }

  /** displayWith wrapper to be null-safe */
  internalDisplayWith = (item: T | null) => (item ? this.displayValue(item) : '');

  /** User typed: invalidate selection (set parent control to null) and update text */
  handleInput(text: string) {
    if (this.inputControl.value !== null) {
      this.inputControl.setValue(null); // parent becomes invalid until a real selection
      this.inputControl.markAsDirty();
    }
    this.textCtrl.setValue(text, { emitEvent: true, onlySelf: true });
  }

  /** User picked an option: set the selected object on the parent control */
  handleOptionSelected(option: T) {
    this.inputControl.setValue(option);
    this.inputControl.markAsDirty();
    this.inputControl.markAsTouched();
    this.textCtrl.setValue(this.displayValue(option), { emitEvent: false });
  }

  /** Blur: mark touched so errors can show, and ping parent */
  handleBlur() {
    if (this.inputControl.value === null) {
      this.textCtrl.setValue('', { emitEvent: true, onlySelf: true });
    }

    this.inputControl.markAsTouched();
    this.onBlur.emit();
  }

  /** Filtering logic */
  private filterOptions(term: string, opts: T[]): T[] {
    const q = (term || '').toLowerCase().trim();
    if (!q) return opts;
    return opts.filter((v) => this.displayValue(v).toLowerCase().includes(q));
  }
}
